import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Rating, 
  Snackbar,
  Alert,
  InputAdornment,
  useTheme,
  alpha
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { submitFeedback } from '../feedbackApi'; // ✅ Import the same API function
import config from '../config';

const PrePaymentFeedback = ({ onSubmitted, order, stageName }) => {
  const theme = useTheme();
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [tip, setTip] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (rating === 0 || loading) return;
    setLoading(true);
    setError(null);
    try {
      // ✅ Get userId from localStorage (EXACT SAME LOGIC)
      const user = (() => {
        try {
          return JSON.parse(localStorage.getItem('user'));
        } catch {
          return null;
        }
      })();
      const userId = user?.Id;
      const customerId = order.CustomerId || order.customerId || userId;
      const orderId = order.OrderId || order.orderId || order.Id;
      
      // ✅ Submit feedback using the same API function
      await submitFeedback({ rating: Number(rating), comment: feedback, orderId, userId });

      // ✅ Update order status to Completed (changed from Delivered since this is after delivery)
      await fetch(`${config.API_BASE_URL}/Orders/UpdateStatus`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ OrderId: orderId, NewStatus: 'Completed', CustomerId: customerId })
      });

      // ✅ Call onSubmitted callback if provided
      if (onSubmitted) {
        onSubmitted({ rating, feedback, tip });
      }
      
      setSubmitted(true);
      setOpenSnackbar(true);
    } catch (err) {
      setError('فشل إرسال التقييم. حاول مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{
        bgcolor: alpha(theme.palette.background.paper, 0.9),
        backdropFilter: 'blur(10px)',
        borderRadius: '24px',
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
        p: { xs: 3, md: 4 },
        m: '2rem auto',
        maxWidth: 600,
        textAlign: 'center',
        border: `1px solid ${alpha(theme.palette.custom?.white || '#fff', 0.2)}`
      }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <StarIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2, animation: 'bounce 2s infinite' }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>شكراً لتقييمك!</Typography>
          <Typography variant="body1" color="text.secondary">رأيك يهمنا ويساعدنا على تحسين خدماتنا.</Typography>
        </Box>
        <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
          <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
            تم إرسال تقييمك بنجاح!
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box sx={{
      bgcolor: alpha(theme.palette.background.paper, 0.9),
      backdropFilter: 'blur(10px)',
      borderRadius: '24px',
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      p: { xs: 3, md: 4 },
      m: '2rem auto',
      maxWidth: 600,
      border: `1px solid ${alpha(theme.palette.custom?.white || '#fff', 0.2)}`
    }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontSize: '2rem', fontWeight: 800, mb: 2, color: 'text.primary' }}>
          قيم تجربتك
        </Typography>
        <Typography variant="body1" color="text.secondary">
          كيف كانت تجربتك مع هذا الطلب؟
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
        <Typography component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
          تقييم الطلب
        </Typography>
        <Rating
          name="simple-controlled"
          value={Number(rating)}
          onChange={(event, newValue) => {
            setRating(newValue);
          }}
          size="large"
          sx={{ fontSize: '3rem' }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          label="ملاحظاتك"
          placeholder="اكتب تعليقك هنا..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          sx={{ 
            bgcolor: 'background.paper', 
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          type="number"
          variant="outlined"
          label="إكرامية للسائق (اختياري)"
          value={tip}
          onChange={(e) => setTip(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AttachMoneyIcon />
              </InputAdornment>
            ),
            endAdornment: <InputAdornment position="end">ريال</InputAdornment>,
          }}
          sx={{ 
            bgcolor: 'background.paper', 
            borderRadius: 2
          }}
        />
      </Box>

      <Button 
        variant="contained" 
        color="primary" 
        fullWidth 
        size="large" 
        onClick={handleSubmit}
        disabled={rating === 0 || loading}
        sx={{ borderRadius: 4, py: 1.5, fontWeight: 'bold', fontSize: '1.1rem' }}
      >
        {loading ? 'جاري الإرسال...' : 'إرسال التقييم'}
      </Button>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      )}

      <style>
        {`
          @keyframes bounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default PrePaymentFeedback;
